import { useEffect } from 'react'
import { Router } from './Router'
import { Toaster } from 'sonner'
import { ConfigProvider } from 'antd'
import { TooltipProvider } from '@/components/ui/tooltip'
import { BackgroundTasksIndicator } from '@/components/background-tasks'
import { useUIStore } from '@/stores/ui-store'

// Brand-aligned Ant Design theme — purple primary per FreighAI brand book
const antdTheme = {
  token: {
    colorPrimary: '#A259FF',
    colorPrimaryHover: '#8B3FE0',
    colorPrimaryActive: '#8B3FE0',
    colorInfo: '#A259FF',
    colorLink: '#A259FF',
    colorLinkHover: '#8B3FE0',
    colorSuccess: '#2CC295',
    colorError: '#EF6461',
    borderRadius: 6,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
}

/**
 * Apply theme to DOM
 * Adds/removes .dark class on <html> element based on theme setting
 */
function useThemeSync() {
  const theme = useUIStore((state) => state.theme)

  useEffect(() => {
    const root = window.document.documentElement

    // Remove existing theme class
    root.classList.remove('dark')

    if (theme === 'dark') {
      root.classList.add('dark')
    } else if (theme === 'system') {
      // Check system preference
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      if (systemPrefersDark) {
        root.classList.add('dark')
      }
    }
    // 'light' theme - no class needed (default)
  }, [theme])

  // Listen for system preference changes when theme is 'system'
  useEffect(() => {
    if (theme !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      const root = window.document.documentElement
      if (e.matches) {
        root.classList.add('dark')
      } else {
        root.classList.remove('dark')
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme])
}

export function App() {
  // Sync theme state to DOM
  useThemeSync()

  return (
    <ConfigProvider theme={antdTheme}>
      <TooltipProvider>
        <Router />
        {/* Toast Notifications */}
        <Toaster
          position="top-center"
          toastOptions={{
            style: {
              background: 'var(--card)',
              border: '1px solid var(--border)',
              color: 'var(--foreground)',
            },
          }}
          richColors
          closeButton
        />
        <BackgroundTasksIndicator />
      </TooltipProvider>
    </ConfigProvider>
  )
}
